/**
 * Overview command center map — fleet markers from GET /devices/map-snapshot (FE-003).
 * Responder locations (ZD-TRANSPORT one-stream · ZD-PARTIAL-F13 freeze):
 *   Live pins = SSE GET /locations/stream;
 *   Soft fallback = GET /locations/latest poll @ 5s only after SSE exits.
 * People strip @ 12s is a Soft UI client of the same SoR (not a second Live path).
 * Normative: docs/reference/spa-transport-duals-freeze.md
 */

import { apiBase } from "../../api.js";
import {
  buildLocationsLatestPath,
  buildLocationsStreamPath,
  LOCATIONS_CONSOLE_TIMEOUT_MS,
} from "../../locations-console-api.js";
import { pumpSseBody } from "../../sse.js";
import { ensureMapLibreLoaded } from "../../ops-map-loader.js";
import { softDeviceLabel, softOperatorNote } from "../../format.js";
import {
  clampOpsMapZoom,
  getOpsMapDefaultCenterZoom,
  isOpsMapMalaysiaRegion,
  OPS_MAP_MIN_ZOOM,
  readOpsMapFlyToFromWindow,
  stripOpsMapFlyQueryFromHash,
} from "../../ops-map-limits.js";
import {
  createCommandCenterMap,
  buildCcMarkerElement,
  updateCcMarkerElement,
  updateCcMarkerAiThreat,
} from "../map/CommandMap.js";
import { applyLocationPinToMarkerEl } from "../../location-pin-lattice.js";
import { buildSpatialAiWorkbenchHtml, softHaversineM } from "../../spatial-ai-workbench.js";
import {
  buildGetAiIncidentPath,
  buildPostAiVisionAskPath,
  buildPostAiVisionRefreshPath,
} from "../../intelligence-console-api.js";
import { syncMapAiWarnAnchor } from "../../map-ai-alert-stage.js";

/** @type {null | (() => void)} */
let overviewMapTeardown = null;

const LOCATIONS_POLL_MS = 5000;
/** Home map: never mount 10k HTML markers (a11y + GPU). Prefer alert/online. */
const OVERVIEW_MAP_MARKER_CAP = 80;

function bearerHeader() {
  const tokenFn = typeof globalThis.getToken === "function" ? globalThis.getToken : null;
  const tok = tokenFn ? String(tokenFn() || "").trim() : "";
  return tok ? { Authorization: "Bearer " + tok } : {};
}

function initialsFromLabel(label) {
  const s = String(label || "").trim();
  if (!s) return "?";
  const parts = s.split(/[\s._-]+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  return s.slice(0, 2).toUpperCase();
}

function createUserLocationMarkerEl(userId, opts = {}) {
  const stale = !!opts.isStale;
  const soft = String(opts.label || userId || "Person").trim() || "Person";
  const el = document.createElement("div");
  el.className = stale
    ? "overview-user-location-marker overview-user-location-marker--stale"
    : "overview-user-location-marker overview-user-location-marker--live";
  el.title = stale
    ? `${soft} · offline / stale GPS · click to Assign`
    : `${soft} · live GPS · click to Assign`;
  el.setAttribute("data-stale", stale ? "1" : "0");
  el.setAttribute("data-user-id", String(userId || ""));
  el.tabIndex = 0;
  el.setAttribute("role", "button");
  el.setAttribute("aria-label", el.title);

  const face = document.createElement("div");
  face.className = "overview-user-location-marker__face";
  const av = String(opts.avatarUrl || "").trim();
  if (av) {
    const img = document.createElement("img");
    img.className = "overview-user-location-marker__img";
    img.alt = "";
    img.decoding = "async";
    img.src = av;
    img.addEventListener("error", () => {
      try {
        img.remove();
        face.textContent = initialsFromLabel(soft);
      } catch (_) {}
    });
    face.appendChild(img);
  } else {
    face.textContent = initialsFromLabel(soft);
  }

  const ring = document.createElement("span");
  ring.className = "overview-user-location-marker__ring";
  ring.setAttribute("aria-hidden", "true");

  const label = document.createElement("span");
  label.className = "overview-user-location-marker__label";
  label.textContent = soft;

  const status = document.createElement("span");
  status.className = "overview-user-location-marker__status";
  status.textContent = stale ? "Offline" : "Live";

  el.appendChild(ring);
  el.appendChild(face);
  el.appendChild(label);
  el.appendChild(status);
  return el;
}

export function runGlobalOverviewMapTeardownIfAny() {
  if (typeof overviewMapTeardown === "function") {
    try {
      overviewMapTeardown();
    } catch (_) {}
    overviewMapTeardown = null;
  }
}

/**
 * @param {unknown[]} items
 */
export function filterMapSnapshotWithCoords(items) {
  const out = [];
  for (const raw of Array.isArray(items) ? items : []) {
    const row = /** @type {Record<string, unknown>} */ (raw);
    const lat = Number(row.map_lat);
    const lng = Number(row.map_lng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) continue;
    out.push(row);
  }
  return out;
}

/**
 * Alarm coords from AI incident evidence (map-snapshot omits device_id).
 * @param {object|null|undefined} plan
 * @returns {{ lat: number, lng: number } | null}
 */
export function planAlarmCoords(plan) {
  if (!plan || typeof plan !== "object") return null;
  const ev =
    (plan.evidence_json && typeof plan.evidence_json === "object" && plan.evidence_json) ||
    (plan.evidence && typeof plan.evidence === "object" && plan.evidence) ||
    {};
  const tel =
    (plan.telemetry && typeof plan.telemetry === "object" && plan.telemetry) ||
    (plan.telemetry_json && typeof plan.telemetry_json === "object" && plan.telemetry_json) ||
    {};
  const lat = Number(
    ev.alarm_lat != null
      ? ev.alarm_lat
      : plan.map_lat != null
        ? plan.map_lat
        : plan.lat != null
          ? plan.lat
          : tel.lat,
  );
  const lng = Number(
    ev.alarm_lng != null
      ? ev.alarm_lng
      : plan.map_lng != null
        ? plan.map_lng
        : plan.lng != null
          ? plan.lng
          : tel.lng,
  );
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null;
  return { lat, lng };
}

/**
 * Nearest opaque marker_ref within maxM (map-snapshot ↔ AI plan join).
 * @param {Map<string, { lat: number, lng: number, label?: string }>} coordsByRef
 * @param {number} lat
 * @param {number} lng
 * @param {number} [maxM]
 * @returns {string}
 */
export function nearestMarkerRefByCoords(coordsByRef, lat, lng, maxM = 150) {
  let bestRef = "";
  let bestM = Infinity;
  if (!(coordsByRef instanceof Map)) return "";
  for (const [ref, c] of coordsByRef.entries()) {
    if (!c || !Number.isFinite(c.lat) || !Number.isFinite(c.lng)) continue;
    const m = softHaversineM(lat, lng, c.lat, c.lng);
    if (!Number.isFinite(m) || m >= bestM) continue;
    bestM = m;
    bestRef = String(ref || "");
  }
  return Number.isFinite(bestM) && bestM <= maxM ? bestRef : "";
}

/**
 * ONCR crown options for one waiting plan — hypothesis first, risk score as fallback.
 * @param {Record<string, unknown>|null} plan
 */
export function crownOptsFromPlan(plan) {
  if (!plan || typeof plan !== "object") {
    return { aiThreat: false, aiScore: null, aiBand: "", hypothesis: null };
  }
  const raw = plan.risk_score != null ? plan.risk_score : plan.risk != null ? plan.risk : null;
  const score = raw != null ? Number(raw) : null;
  const hyp = plan.hypothesis && typeof plan.hypothesis === "object" ? plan.hypothesis : null;
  return {
    aiThreat: true,
    aiScore: Number.isFinite(score) ? score : null,
    aiBand: "Needs you",
    hypothesis: hyp,
  };
}

/**
 * Cap + prioritize markers for Home (alert → online → offline).
 * @param {Record<string, unknown>[]} items
 * @param {number} [cap]
 */
export function prioritizeOverviewMapMarkers(items, cap = OVERVIEW_MAP_MARKER_CAP) {
  const list = Array.isArray(items) ? items.slice() : [];
  const rank = (row) => {
    const pulse = String(row.map_pulse || (row.is_online ? "online" : "offline"));
    if (pulse === "alert") return 0;
    if (pulse === "online" || row.is_online) return 1;
    return 2;
  };
  list.sort((a, b) => rank(a) - rank(b));
  const limit = Math.max(1, Number(cap) || OVERVIEW_MAP_MARKER_CAP);
  return {
    items: list.slice(0, limit),
    total: list.length,
    truncated: list.length > limit,
  };
}

/**
 * @param {import('maplibre-gl').Map} map
 * @param {Record<string, unknown>[]} items
 */
function fitMapToItems(map, items) {
  if (!map || !items.length) return;
  // Malaysia-first: only auto-frame devices inside the MY product region.
  // Offline/demo fleets elsewhere (e.g. African test pins) must not yank the Home map away.
  let minLat = Infinity;
  let maxLat = -Infinity;
  let minLng = Infinity;
  let maxLng = -Infinity;
  let n = 0;
  for (const row of items) {
    const lat = Number(row.map_lat);
    const lng = Number(row.map_lng);
    if (!Number.isFinite(lat) || !Number.isFinite(lng)) continue;
    if (!isOpsMapMalaysiaRegion(lat, lng)) continue;
    minLat = Math.min(minLat, lat);
    maxLat = Math.max(maxLat, lat);
    minLng = Math.min(minLng, lng);
    maxLng = Math.max(maxLng, lng);
    n += 1;
  }
  if (!n) return;
  try {
    if (n === 1) {
      map.easeTo({ center: [minLng, minLat], zoom: clampOpsMapZoom(14), duration: 500 });
      return;
    }
    map.fitBounds([[minLng, minLat], [maxLng, maxLat]], { padding: 40, maxZoom: 16, duration: 600 });
  } catch (_) {}
}

/**
 * @param {object} opts
 * @param {HTMLElement|null} opts.container
 * @param {() => boolean} opts.isAlive
 * @param {(path: string, o?: object) => Promise<unknown>} opts.api
 * @param {(path?: string) => string} opts.buildMapSnapshotPath
 * @param {(el: HTMLElement|null, html: string) => void} opts.setChildMarkup
 * @param {(s: string) => string} opts.escapeHtml
 * @param {boolean} [opts.locationsEnabled]
 * @param {(msg: string, kind?: string) => void} [opts.toast]
 * @param {(pack: { userId: string, label: string, lat: number, lng: number, stale: boolean }) => void} [opts.onSelectPerson]
 * @param {(pack: { deviceId: string, label: string, lat?: number, lng?: number, markerRef?: string }) => void} [opts.onSelectDevice]
 */
export function createOverviewMapStageController(opts) {
  /** @type {import('maplibre-gl').Map | null} */
  let map = null;
  /** @type {Map<string, import('maplibre-gl').Marker>} */
  const markers = new Map();
  /** @type {Map<string, string>} device_id → marker_ref */
  const deviceRefById = new Map();
  /** @type {Map<string, object>} device_id → waiting AI plan */
  const deviceAiPlanById = new Map();
  /** @type {Map<string, object>} marker_ref → waiting AI plan (join when snapshot hides device_id) */
  const aiPlanByMarkerRef = new Map();
  /** @type {Map<string, { lat: number; lng: number; label: string }>} */
  const markerCoordsByRef = new Map();
  /** @type {Map<string, import('maplibre-gl').Marker>} */
  const userMarkers = new Map();
  /** @type {Map<string, import('maplibre-gl').Marker>} */
  const aiMarkers = new Map();
  /** @type {Map<string, { lat: number; lng: number; label: string }>} */
  const deviceCoords = new Map();
  /** @type {HTMLElement | null} */
  let aiSheet = null;
  /** @type {AbortController | null} */
  let locationsAbort = null;
  /** @type {ReturnType<typeof setInterval> | null} */
  let locationsPollTimer = null;
  let didFit = false;
  let didAiFly = false;
  /** @type {null | (() => void)} */
  let onAiWarnMove = null;
  let didAiAutoOpen = false;

  const clearUserMarkers = () => {
    for (const mk of userMarkers.values()) {
      try {
        mk.remove();
      } catch (_) {}
    }
    userMarkers.clear();
  };

  /** Soft chip when shared GPS people sit outside the current camera. */
  const syncPeopleOffMapChip = () => {
    const stage = opts.container && opts.container.closest && opts.container.closest(".croc-cc-map-stage");
    if (!stage || !map) return;
    let chip = stage.querySelector("[data-people-offmap-chip]");
    const off = [];
    try {
      const bounds = typeof map.getBounds === "function" ? map.getBounds() : null;
      for (const p of lastPeopleRows) {
        if (!p || !Number.isFinite(p.lat) || !Number.isFinite(p.lng)) continue;
        if (bounds && typeof bounds.contains === "function" && bounds.contains([p.lng, p.lat])) continue;
        off.push(p);
      }
    } catch (_) {
      return;
    }
    if (!off.length) {
      if (chip) chip.hidden = true;
      return;
    }
    if (!chip) {
      chip = document.createElement("button");
      chip.type = "button";
      chip.className = "croc-cc-people-offmap-chip btn sm btn-tap";
      chip.setAttribute("data-people-offmap-chip", "1");
      stage.appendChild(chip);
    }
    const lead = off[0];
    chip.hidden = false;
    chip.textContent = off.length === 1
      ? `${lead.label || lead.id} off map · fly`
      : `${off.length} people off map · fly`;
    chip.title = "Fly to shared phone location (stale pins stay labeled)";
    chip.onclick = () => {
      if (!lead || !Number.isFinite(lead.lng) || !Number.isFinite(lead.lat)) return;
      try {
        map.flyTo({
          center: [lead.lng, lead.lat],
          zoom: clampOpsMapZoom(Math.max(Number(map.getZoom?.() || 0), 14)),
          essential: true,
        });
      } catch (_) {}
      if (typeof opts.onSelectPerson === "function") {
        opts.onSelectPerson({
          userId: lead.id,
          label: lead.label || lead.id,
          lat: lead.lat,
          lng: lead.lng,
          stale: !!lead.stale,
        });
      }
    };
  };

  const stopLocationsLayer = () => {
    if (locationsAbort) {
      try {
        locationsAbort.abort();
      } catch (_) {}
      locationsAbort = null;
    }
    if (locationsPollTimer) {
      clearInterval(locationsPollTimer);
      locationsPollTimer = null;
    }
    clearUserMarkers();
  };

  const syncUserLocations = (items) => {
    if (!map || !opts.isAlive()) return;
    const maplibregl = typeof window !== "undefined" ? window.maplibregl : null;
    if (!maplibregl) return;
    const seen = new Set();
    /** @type {typeof lastPeopleRows} */
    const people = [];
    for (const raw of Array.isArray(items) ? items : []) {
      const row = /** @type {Record<string, unknown>} */ (raw);
      const userId = String(row.user_id || row.username || "").trim();
      const lat = Number(row.latitude ?? row.lat);
      const lng = Number(row.longitude ?? row.lng);
      if (!userId || !Number.isFinite(lat) || !Number.isFinite(lng)) continue;
      seen.add(userId);
      const isStale = !!(row.is_stale === true || row.is_stale === 1 || row.is_stale === "1");
      const softLabel = softDeviceLabel(userId) || String(userId);
      const avatarUrl = String(row.avatar_url || row.avatar || "").trim();
      people.push({ id: userId, label: softLabel, lat, lng, stale: isStale });
      const wirePersonClick = (el) => {
        el.__crocPersonPick = { userId, label: softLabel, lat, lng, stale: isStale };
        if (el.dataset.personClickWired === "1") return;
        el.dataset.personClickWired = "1";
        const openPerson = (ev) => {
          try {
            if (ev && typeof ev.preventDefault === "function") ev.preventDefault();
            if (ev && typeof ev.stopPropagation === "function") ev.stopPropagation();
          } catch (_) {}
          const pack = el.__crocPersonPick || { userId, label: softLabel, lat, lng, stale: isStale };
          try {
            if (map && typeof map.flyTo === "function" && Number.isFinite(pack.lng) && Number.isFinite(pack.lat)) {
              map.flyTo({
                center: [pack.lng, pack.lat],
                zoom: clampOpsMapZoom(Math.max(Number(map.getZoom?.() || 0), 14)),
                essential: true,
              });
            }
          } catch (_) {}
          if (typeof opts.onSelectPerson === "function") {
            opts.onSelectPerson(pack);
            return;
          }
          if (typeof opts.toast === "function") {
            opts.toast(
              pack.stale
                ? `${pack.label || pack.userId} · stale GPS · open Assign to use this person`
                : `${pack.label || pack.userId} · selected for Assign`,
            );
          }
        };
        el.addEventListener("click", openPerson);
        el.addEventListener("keydown", (ev) => {
          if (ev.key === "Enter" || ev.key === " ") openPerson(ev);
        });
      };
      let mk = userMarkers.get(userId);
      if (!mk) {
        const el = createUserLocationMarkerEl(userId, { isStale, label: softLabel, avatarUrl });
        wirePersonClick(el);
        mk = new maplibregl.Marker({ element: el, anchor: "bottom" })
          .setLngLat([lng, lat])
          .addTo(map);
        userMarkers.set(userId, mk);
      } else {
        mk.setLngLat([lng, lat]);
        const el = mk.getElement && mk.getElement();
        if (el) {
          el.classList.toggle("overview-user-location-marker--stale", isStale);
          el.classList.toggle("overview-user-location-marker--live", !isStale);
          el.setAttribute("data-stale", isStale ? "1" : "0");
          el.title = isStale
            ? `${softLabel} · offline / stale GPS · click to Assign`
            : `${softLabel} · live GPS · click to Assign`;
          el.tabIndex = 0;
          el.setAttribute("role", "button");
          el.setAttribute("aria-label", el.title);
          const lab = el.querySelector(".overview-user-location-marker__label");
          if (lab) lab.textContent = softLabel;
          const st = el.querySelector(".overview-user-location-marker__status");
          if (st) st.textContent = isStale ? "Offline" : "Live";
          const face = el.querySelector(".overview-user-location-marker__face");
          if (face && !face.querySelector("img")) face.textContent = initialsFromLabel(softLabel);
          const oldBadge = el.querySelector(".overview-user-location-marker__stale");
          if (oldBadge) {
            try { oldBadge.remove(); } catch (_) {}
          }
          wirePersonClick(el);
        }
      }
    }
    lastPeopleRows = people;
    try {
      syncPeopleOffMapChip();
    } catch (_) {}
    for (const [uid, mk] of userMarkers.entries()) {
      if (!seen.has(uid)) {
        try {
          mk.remove();
        } catch (_) {}
        userMarkers.delete(uid);
      }
    }
    // Refresh open Execute sheet so Soft candidates stay current.
    try {
      if (aiSheet && !aiSheet.hidden && String(aiSheet.dataset.activeTab || "") === "execution") {
        const openId = String(aiSheet.dataset.planId || "");
        const cur = (Array.isArray(lastAiAll) ? lastAiAll : []).find(
          (p) => String((p && (p.incident_id || p.id || p.item_id)) || "") === openId,
        );
        if (cur) {
          paintAiSheet(cur, "execution", aiDetailCache.get(openId) || null);
        }
      }
    } catch (_) {}
  };

  const refreshLocationsPoll = async () => {
    if (!opts.isAlive() || !opts.locationsEnabled) return;
    try {
      const res = await opts.api(buildLocationsLatestPath(), { timeoutMs: LOCATIONS_CONSOLE_TIMEOUT_MS });
      if (!opts.isAlive()) return;
      syncUserLocations((res && /** @type {{ items?: unknown[] }} */ (res).items) || []);
    } catch (_) {}
  };

  const startLocationsPolling = () => {
    if (locationsPollTimer) clearInterval(locationsPollTimer);
    void refreshLocationsPoll();
    locationsPollTimer = setInterval(() => {
      if (opts.isAlive()) void refreshLocationsPoll();
    }, LOCATIONS_POLL_MS);
  };

  const startLocationsLayer = () => {
    if (!opts.locationsEnabled) return;
    stopLocationsLayer();
    locationsAbort = new AbortController();
    const ac = locationsAbort;
    const run = async () => {
      while (!ac.signal.aborted && opts.isAlive()) {
        try {
          const r = await fetch(apiBase() + buildLocationsStreamPath(), {
            method: "GET",
            credentials: "include",
            headers: bearerHeader(),
            signal: ac.signal,
          });
          if (ac.signal.aborted || !opts.isAlive()) return;
          if (!r.ok || !r.body || typeof r.body.getReader !== "function") break;
          await pumpSseBody(r.body.getReader(), ac.signal, (kind, payload) => {
            if (kind === "ping" || ac.signal.aborted || !opts.isAlive()) return;
            try {
              const data = JSON.parse(payload);
              syncUserLocations((data && data.items) || []);
            } catch (_) {}
          });
          if (ac.signal.aborted || !opts.isAlive()) return;
          await new Promise((resolve) => setTimeout(resolve, 1500));
        } catch (exc) {
          if (exc && exc.name === "AbortError") return;
          break;
        }
      }
      if (!ac.signal.aborted && opts.isAlive()) startLocationsPolling();
    };
    void run();
  };

  const clearAiMarkers = () => {
    for (const mk of aiMarkers.values()) {
      try {
        mk.remove();
      } catch (_) {}
    }
    aiMarkers.clear();
    if (aiSheet) {
      try {
        aiSheet.remove();
      } catch (_) {}
      aiSheet = null;
    }
  };

  const ensureAiSheet = () => {
    const stage = opts.container && opts.container.closest(".croc-cc-map-stage");
    if (!stage) return null;
    if (aiSheet && stage.contains(aiSheet)) return aiSheet;
    const el = document.createElement("div");
    el.className = "croc-cc-ai-map-sheet glass-panel";
    el.hidden = true;
    el.setAttribute("role", "dialog");
    el.setAttribute("aria-label", "Auto Sentinel plan on map");
    stage.appendChild(el);
    aiSheet = el;
    return el;
  };

  /** @type {unknown[]} */
  let lastAiWaiting = [];
  /** @type {unknown[]} */
  let lastAiAll = [];
  /** @type {Array<{ id: string, label: string, lat: number, lng: number, stale: boolean }>} */
  let lastPeopleRows = [];
  /** @type {Map<string, object>} */
  const aiDetailCache = new Map();

  const fieldCandidatesForPlan = (plan) => {
    // ZD-PERM Soft: re-rank only API-visible people already on the map
    // (SSE/latest → visible_phone_location_usernames). Never invent users.
    const deviceId = String((plan && plan.device_id) || "").trim();
    const self = deviceId ? deviceCoords.get(deviceId) : null;
    if (!self || !Number.isFinite(self.lat) || !Number.isFinite(self.lng)) return [];
    /** @type {Array<{ kind: string, label: string, id: string, meters: number, stale?: boolean, note?: string, reason?: string, source?: string, rank?: number }>} */
    const out = [];
    for (const p of lastPeopleRows) {
      if (!p || !Number.isFinite(p.lat) || !Number.isFinite(p.lng)) continue;
      // CONV-4: Fresh-only Soft fallback — stale GPS must not win nearest.
      if (p.stale) continue;
      const meters = softHaversineM(self.lat, self.lng, p.lat, p.lng);
      if (!Number.isFinite(meters)) continue;
      out.push({
        kind: "Person",
        label: p.label || p.id,
        id: p.id,
        meters,
        stale: false,
        note: "shared GPS Fresh-only Soft",
        source: "fe_soft",
      });
    }
    for (const [did, c] of deviceCoords.entries()) {
      if (!did || did === deviceId || !c) continue;
      if (!Number.isFinite(c.lat) || !Number.isFinite(c.lng)) continue;
      const meters = softHaversineM(self.lat, self.lng, c.lat, c.lng);
      if (!Number.isFinite(meters) || meters > 25000) continue;
      out.push({
        kind: "Device",
        label: c.label || did,
        id: did,
        meters,
        note: "fleet Soft",
        source: "fe_soft",
      });
    }
    out.sort((a, b) => a.meters - b.meters);
    return out.slice(0, 6);
  };

  const serverCandidatesFromDetail = (detail) => {
    if (!detail || typeof detail !== "object") return { list: [], note: "" };
    const ev =
      (detail.evidence && typeof detail.evidence === "object" && detail.evidence) ||
      (detail.evidence_json && typeof detail.evidence_json === "object" && detail.evidence_json) ||
      {};
    const list = Array.isArray(ev.execution_candidates) ? ev.execution_candidates : [];
    const note = String(ev.execution_candidates_note || "").trim();
    const mapped = list
      .map((c, i) => {
        if (!c || typeof c !== "object") return null;
        const meters =
          Number.isFinite(Number(c.meters))
            ? Number(c.meters)
            : Number.isFinite(Number(c.distance_km))
              ? Math.round(Number(c.distance_km) * 1000)
              : NaN;
        return {
          kind: String(c.kind || "Person"),
          label: String(c.label || c.id || `Candidate ${i + 1}`),
          id: String(c.id || ""),
          meters: Number.isFinite(meters) ? meters : 0,
          stale: !!c.stale,
          note: String(c.reason || c.note || "server Top-N"),
          reason: String(c.reason || ""),
          source: String(c.source || "server_topn_haversine"),
          rank: Number(c.rank) || i + 1,
        };
      })
      .filter(Boolean)
      .slice(0, 6);
    return { list: mapped, note, hasServerPack: Array.isArray(ev.execution_candidates) };
  };

  const historyForPlan = (plan) => {
    const deviceId = String((plan && plan.device_id) || "").trim();
    const id = String((plan && (plan.incident_id || plan.id || plan.item_id)) || "").trim();
    const pool = Array.isArray(lastAiAll) && lastAiAll.length ? lastAiAll : lastAiWaiting;
    return (Array.isArray(pool) ? pool : []).filter((h) => {
      const hid = String((h && (h.incident_id || h.id || h.item_id)) || "").trim();
      const did = String((h && h.device_id) || "").trim();
      if (!hid || hid === id) return false;
      if (deviceId && did && did !== deviceId) return false;
      return true;
    });
  };

  const unwrapAiDetail = (pack) => {
    if (!pack || typeof pack !== "object") return null;
    if (pack.record && typeof pack.record === "object") return pack.record;
    if (pack.item && typeof pack.item === "object") return pack.item;
    return pack;
  };

  const paintAiSheet = (plan, activeTab = "decision", detail = null) => {
    const el = ensureAiSheet();
    if (!el) return;
    const id = String((plan && (plan.incident_id || plan.id || plan.item_id)) || "").trim();
    const serverPack = serverCandidatesFromDetail(detail);
    const serverCandidates = serverPack.list || [];
    const softCandidates = fieldCandidatesForPlan(plan);
    // AI-DISP-5: when server evidence pack exists, never soft-fake a Top-N list.
    const fieldCandidates = serverCandidates.length
      ? serverCandidates
      : serverPack.hasServerPack
        ? []
        : softCandidates;
    el.innerHTML = buildSpatialAiWorkbenchHtml(plan, {
      escapeHtml: opts.escapeHtml,
      activeTab,
      detail,
      history: historyForPlan(plan),
      fieldCandidates,
      candidatesNote: serverPack.note || "",
    });
    el.hidden = false;
    el.dataset.planId = id;
    el.dataset.activeTab = String(activeTab || "decision");
    try {
      const stage = el.closest(".croc-cc-map-stage");
      if (stage) stage.classList.add("croc-cc-map-stage--saw");
    } catch (_) {}
    if (!el.dataset.sawWired) {
      el.dataset.sawWired = "1";
      el.addEventListener("click", (ev) => {
        const t = ev.target && ev.target.closest
          ? ev.target.closest(
              "[data-saw-tab],[data-saw-open-plan],[data-ai-map-sheet-close],[data-saw-frame-ask],[data-saw-frame-refresh]",
            )
          : null;
        if (!t || !el.contains(t)) return;
        if (t.getAttribute("data-ai-map-sheet-close")) {
          el.hidden = true;
          try {
            const stage = el.closest(".croc-cc-map-stage");
            if (stage) stage.classList.remove("croc-cc-map-stage--saw");
          } catch (_) {}
          return;
        }
        const askId = t.getAttribute("data-saw-frame-ask");
        if (askId && opts.api) {
          const input = el.querySelector(`input[data-saw-frame-q="${askId.replace(/"/g, "")}"]`);
          const q = String((input && input.value) || "").trim();
          if (!q) {
            if (typeof opts.toast === "function") opts.toast("Type a frame question first");
            return;
          }
          (async () => {
            try {
              const pack = await opts.api(buildPostAiVisionAskPath(askId), {
                method: "POST",
                body: { question: q },
                timeoutMs: 45000,
                retries: 0,
              });
              if (!opts.isAlive()) return;
              const rec = pack && pack.record && typeof pack.record === "object" ? pack.record : null;
              if (rec) {
                aiDetailCache.set(askId, rec);
                const pool = Array.isArray(lastAiAll) && lastAiAll.length ? lastAiAll : lastAiWaiting;
                const cur = (Array.isArray(pool) ? pool : []).find(
                  (p) => String((p && (p.incident_id || p.id || p.item_id)) || "") === askId,
                ) || { incident_id: askId };
                paintAiSheet(cur, "frame", rec);
              }
              if (typeof opts.toast === "function") {
                opts.toast(
                  pack && pack.answered
                    ? "Frame answer ready"
                    : softOperatorNote((pack && (pack.reason || pack.honesty)) || "Frame ask empty"),
                );
              }
            } catch (_err) {
              if (typeof opts.toast === "function") opts.toast("Frame ask API/OCR failed");
            }
          })();
          return;
        }
        const refreshId = t.getAttribute("data-saw-frame-refresh");
        if (refreshId && opts.api) {
          (async () => {
            try {
              const pack = await opts.api(buildPostAiVisionRefreshPath(refreshId), {
                method: "POST",
                body: { force: true },
                timeoutMs: 45000,
                retries: 0,
              });
              if (!opts.isAlive()) return;
              const rec = pack && pack.record && typeof pack.record === "object" ? pack.record : null;
              if (rec) {
                aiDetailCache.set(refreshId, rec);
                const pool = Array.isArray(lastAiAll) && lastAiAll.length ? lastAiAll : lastAiWaiting;
                const cur = (Array.isArray(pool) ? pool : []).find(
                  (p) => String((p && (p.incident_id || p.id || p.item_id)) || "") === refreshId,
                ) || { incident_id: refreshId };
                paintAiSheet(cur, "frame", rec);
              }
              if (typeof opts.toast === "function") {
                opts.toast(pack && pack.refreshed ? "OCR refreshed" : "OCR already present");
              }
            } catch (_err) {
              if (typeof opts.toast === "function") opts.toast("OCR refresh API failed");
            }
          })();
          return;
        }
        const openPlan = t.getAttribute("data-saw-open-plan");
        if (openPlan) {
          const pool = Array.isArray(lastAiAll) && lastAiAll.length ? lastAiAll : lastAiWaiting;
          const next = (Array.isArray(pool) ? pool : []).find(
            (p) => String((p && (p.incident_id || p.id || p.item_id)) || "") === openPlan,
          );
          if (next) openAiSheet(next, "decision");
          return;
        }
        const tab = t.getAttribute("data-saw-tab");
        if (tab) {
          const curId = String(el.dataset.planId || "");
          const pool = Array.isArray(lastAiAll) && lastAiAll.length ? lastAiAll : lastAiWaiting;
          const cur = (Array.isArray(pool) ? pool : []).find(
            (p) => String((p && (p.incident_id || p.id || p.item_id)) || "") === curId,
          ) || { incident_id: curId };
          const cached = aiDetailCache.get(curId) || null;
          paintAiSheet(cur, tab, cached);
          // Revalidate when switching tabs so Decision/Execute see fresh status.
          if (curId && opts.api) openAiSheet(cur, tab, { force: false });
        }
      });
      el.addEventListener("keydown", (ev) => {
        const input = ev.target && ev.target.getAttribute
          ? ev.target
          : null;
        if (!input || !input.getAttribute("data-saw-frame-q")) return;
        if (ev.key !== "Enter") return;
        ev.preventDefault();
        const askId = String(input.getAttribute("data-saw-frame-q") || "").trim();
        const btn = askId ? el.querySelector(`[data-saw-frame-ask="${askId.replace(/"/g, "")}"]`) : null;
        if (btn) btn.click();
      });
    }
  };

  /** One auto OCR refresh per plan open (implant AI · backend G3b). */
  const visionAutoTried = new Set();

  /**
   * @param {object} plan
   * @param {string} [activeTab]
   * @param {{ force?: boolean; detail?: object|null }} [opts2]
   */
  const openAiSheet = (plan, activeTab = "decision", opts2 = {}) => {
    const id = String((plan && (plan.incident_id || plan.id || plan.item_id)) || "").trim();
    const force = !!(opts2 && opts2.force);
    const seed = opts2 && opts2.detail && typeof opts2.detail === "object" ? opts2.detail : null;
    if (id && seed) aiDetailCache.set(id, seed);
    const cached = id && !force ? aiDetailCache.get(id) : null;
    paintAiSheet(plan, activeTab, seed || cached || null);
    if (!id || !opts.api) return;
    // Stale-while-revalidate: always re-fetch so enrich/dispatch/evidence stay honest.
    (async () => {
      try {
        const pack = await opts.api(buildGetAiIncidentPath(id), { timeoutMs: 12000, retries: 0 });
        if (!opts.isAlive()) return;
        const detail = unwrapAiDetail(pack);
        if (detail && typeof detail === "object") {
          aiDetailCache.set(id, detail);
          const el = aiSheet;
          if (el && !el.hidden && String(el.dataset.planId || "") === id) {
            paintAiSheet(plan, String(el.dataset.activeTab || activeTab || "decision"), detail);
          }
          // Implant: if snapshot linked but OCR missing, call real vision/refresh once.
          const ev =
            detail.evidence && typeof detail.evidence === "object"
              ? detail.evidence
              : detail.evidence_json && typeof detail.evidence_json === "object"
                ? detail.evidence_json
                : {};
          const hasSnap = !!(ev.snapshot_id || detail.snapshot_id);
          const hasVision = !!(ev.vision_analyzed && ev.vision_summary);
          if (hasSnap && !hasVision && !visionAutoTried.has(id)) {
            visionAutoTried.add(id);
            try {
              const refreshed = await opts.api(buildPostAiVisionRefreshPath(id), {
                method: "POST",
                body: { force: false },
                timeoutMs: 45000,
                retries: 0,
              });
              if (!opts.isAlive()) return;
              const rec =
                refreshed && refreshed.record && typeof refreshed.record === "object"
                  ? refreshed.record
                  : null;
              if (rec) {
                aiDetailCache.set(id, rec);
                const sheet = aiSheet;
                if (sheet && !sheet.hidden && String(sheet.dataset.planId || "") === id) {
                  paintAiSheet(plan, String(sheet.dataset.activeTab || activeTab || "decision"), rec);
                }
              }
            } catch (_) {}
          }
        }
      } catch (_) {}
    })();
  };

  /** Keep map workbench open after Allow/Hold with server record seed. */
  const focusAiPlan = (planOrId, activeTab = "execution", detail = null) => {
    const id =
      typeof planOrId === "string"
        ? String(planOrId || "").trim()
        : String((planOrId && (planOrId.incident_id || planOrId.id || planOrId.item_id)) || "").trim();
    if (!id) return;
    const fromAll = (Array.isArray(lastAiAll) ? lastAiAll : []).find(
      (p) => String((p && (p.incident_id || p.id || p.item_id)) || "") === id,
    );
    const plan =
      (typeof planOrId === "object" && planOrId) ||
      fromAll ||
      (detail && typeof detail === "object" ? detail : null) ||
      { incident_id: id };
    openAiSheet(plan, activeTab, { force: true, detail: detail || null });
  };

  const teardown = () => {
    stopLocationsLayer();
    clearAiMarkers();
    deviceCoords.clear();
    for (const mk of markers.values()) {
      try {
        mk.remove();
      } catch (_) {}
    }
    markers.clear();
    deviceRefById.clear();
    deviceAiPlanById.clear();
    aiPlanByMarkerRef.clear();
    markerCoordsByRef.clear();
    if (map && onAiWarnMove) {
      try {
        map.off("move", onAiWarnMove);
        map.off("zoom", onAiWarnMove);
        map.off("resize", onAiWarnMove);
      } catch (_) {}
    }
    onAiWarnMove = null;
    if (map) {
      try {
        const runtime = typeof window !== "undefined" ? window.CrocGeoLibre : null;
        if (runtime && map.__crocReactRootContainer) {
          runtime.unmountMap(map.__crocReactRootContainer);
        } else {
          map.remove();
        }
      } catch (_) {}
      map = null;
    }
    try {
      if (typeof window !== "undefined" && window.__crocCcMap) window.__crocCcMap = null;
    } catch (_) {}
    didFit = false;
    didAiFly = false;
    didAiAutoOpen = false;
    const stage = opts.container && opts.container.closest(".croc-cc-map-stage");
    if (stage) stage.classList.remove("croc-cc-map-stage--live");
  };

  runGlobalOverviewMapTeardownIfAny();
  overviewMapTeardown = () => {
    teardown();
    overviewMapTeardown = null;
  };

  const syncMarkers = (items, meta) => {
    if (!map || !opts.isAlive()) return;
    const maplibregl = typeof window !== "undefined" ? window.maplibregl : null;
    if (!maplibregl) return;
    // GeoLibre data plane: DuckDB-WASM normalizes/query-filters the fleet,
    // then deck.gl renders the scalable device field in the shared map context.
    try {
      const geo = map.__crocGeoLibre;
      if (geo && typeof geo.setDeviceRows === "function") {
        clearTimeout(geo.deviceRowsTimer);
        geo.deviceRowsTimer = setTimeout(() => {
          geo.setDeviceRows(items).catch((error) => {
            console.warn("[GeoLibre] device query/render skipped", error && error.message);
          });
        }, 180);
      }
    } catch (_) {}
    const seen = new Set();
    deviceCoords.clear();
    deviceRefById.clear();
    markerCoordsByRef.clear();
    for (const row of items) {
      const lat = Number(row.map_lat);
      const lng = Number(row.map_lng);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) continue;
      const ref = String(row.marker_ref || row.device_id || "").trim() || `${lat},${lng}`;
      const deviceId = String(row.device_id || "").trim();
      const label = (() => {
        const raw = String(row.label || "").trim();
        if (raw) return raw;
        try {
          if (typeof softDeviceLabel === "function") return softDeviceLabel(row.device_id);
        } catch (_) {}
        return "Unit";
      })();
      markerCoordsByRef.set(ref, { lat, lng, label });
      if (deviceId) {
        deviceCoords.set(deviceId, { lat, lng, label });
        deviceRefById.set(deviceId, ref);
      }
      seen.add(ref);
      const aiPlan =
        aiPlanByMarkerRef.get(ref) ||
        (deviceId ? deviceAiPlanById.get(deviceId) : null) ||
        null;
      if (aiPlan) {
        const did = String((aiPlan && aiPlan.device_id) || deviceId || "").trim();
        if (did) {
          deviceCoords.set(did, { lat, lng, label });
          deviceRefById.set(did, ref);
        }
      }
      const pulseRaw = String(row.map_pulse || (row.is_online ? "online" : "offline"));
      let pulse = pulseRaw === "alert" || pulseRaw === "online" ? pulseRaw : "offline";
      if (aiPlan) pulse = "alert";
      const aiOpts = crownOptsFromPlan(aiPlan);
      let mk = markers.get(ref);
      if (!mk) {
        const el = buildCcMarkerElement(label, pulse, aiOpts);
        el.tabIndex = 0;
        el.setAttribute("role", "button");
        el.removeAttribute("aria-hidden");
        el.dataset.markerRef = ref;
        if (deviceId) el.dataset.deviceId = deviceId;
        else if (aiPlan && aiPlan.device_id) el.dataset.deviceId = String(aiPlan.device_id);
        const openDevice = (ev) => {
          ev.preventDefault();
          ev.stopPropagation();
          const mref = String(el.dataset.markerRef || ref || "").trim();
          const did = String(el.dataset.deviceId || deviceId || "").trim();
          const plan =
            (mref && aiPlanByMarkerRef.get(mref)) ||
            (did ? deviceAiPlanById.get(did) : null) ||
            null;
          if (plan) {
            const labeled =
              label && !(plan.display_label || plan.device_label)
                ? { ...plan, display_label: label, device_label: label }
                : plan;
            openAiSheet(labeled, "decision");
            return;
          }
          // No AI plan — fill Assign (or toast) so every pin stays actionable.
          const coord = (mref && markerCoordsByRef.get(mref)) || (did && deviceCoords.get(did)) || null;
          if (typeof opts.onSelectDevice === "function") {
            opts.onSelectDevice({
              deviceId: did,
              label,
              lat: coord && Number.isFinite(coord.lat) ? coord.lat : undefined,
              lng: coord && Number.isFinite(coord.lng) ? coord.lng : undefined,
              markerRef: mref,
            });
            return;
          }
          if (typeof opts.toast === "function") {
            opts.toast(`${label} · selected for Assign`);
          }
        };
        el.addEventListener("click", openDevice);
        el.addEventListener("keydown", (ev) => {
          if (ev.key === "Enter" || ev.key === " ") openDevice(ev);
        });
        mk = new maplibregl.Marker({ element: el, anchor: "center" })
          .setLngLat([lng, lat])
          .addTo(map);
        markers.set(ref, mk);
        try {
          applyLocationPinToMarkerEl(el, row);
        } catch (_) {}
      } else {
        mk.setLngLat([lng, lat]);
        const el = mk.getElement();
        if (el) {
          updateCcMarkerElement(el, label, pulse, aiOpts);
          el.tabIndex = 0;
          el.setAttribute("role", "button");
          el.removeAttribute("aria-hidden");
          el.dataset.markerRef = ref;
          if (deviceId) el.dataset.deviceId = deviceId;
          else if (aiPlan && aiPlan.device_id) el.dataset.deviceId = String(aiPlan.device_id);
          try {
            applyLocationPinToMarkerEl(el, row);
          } catch (_) {}
        }
      }
    }
    for (const [ref, mk] of markers.entries()) {
      if (!seen.has(ref)) {
        try {
          mk.remove();
        } catch (_) {}
        markers.delete(ref);
      }
    }
    if (!didFit && items.length) {
      fitMapToItems(map, items);
      didFit = true;
      // Non-MY demo fleets must not leave the camera at world [0,0].
      try {
        const c = map.getCenter();
        const z = map.getZoom();
        if (!isOpsMapMalaysiaRegion(c.lat, c.lng) || z <= OPS_MAP_MIN_ZOOM + 0.05) {
          const home = getOpsMapDefaultCenterZoom();
          map.jumpTo({
            center: [home.lng, home.lat],
            zoom: clampOpsMapZoom(home.zoom),
            essential: true,
          });
        }
      } catch (_) {}
    }
    try {
      const stage = opts.container && opts.container.closest(".croc-cc-map-stage");
      const ph = stage && stage.querySelector(".croc-cc-map-placeholder");
      if (ph && meta && meta.truncated) {
        ph.textContent = `Showing ${items.length} of ${meta.total} boards on the map · open All boards for the full list.`;
        ph.style.display = "";
        ph.style.pointerEvents = "none";
      } else if (ph && stage && stage.classList.contains("croc-cc-map-stage--armed")) {
        ph.style.display = "none";
      }
    } catch (_) {}
  };

  const init = async () => {
    const container = opts.container;
    if (!container || !opts.isAlive()) return;
    try {
      await ensureMapLibreLoaded();
      if (!opts.isAlive()) return;
      const flyTo = readOpsMapFlyToFromWindow();
      const centerZoom = flyTo
        ? { lng: flyTo.lng, lat: flyTo.lat, zoom: flyTo.zoom }
        : undefined;
      const created = await createCommandCenterMap({
        container,
        pitch3d: false,
        centerZoom,
      });
      map = created.map;
      try {
        if (typeof window !== "undefined") window.__crocCcMap = map;
      } catch (_) {}
      try {
        map.on("moveend", () => {
          try { syncPeopleOffMapChip(); } catch (_) {}
        });
      } catch (_) {}
      onAiWarnMove = () => {
        try {
          const root = (opts.container && opts.container.closest("#view"))
            || (opts.container && opts.container.closest(".croc-cc"))
            || document;
          syncMapAiWarnAnchor(root, map);
        } catch (_) {}
      };
      try {
        map.on("move", onAiWarnMove);
        map.on("zoom", onAiWarnMove);
        map.on("resize", onAiWarnMove);
      } catch (_) {}
      const stage = container.closest(".croc-cc-map-stage");
      if (stage) stage.classList.add("croc-cc-map-stage--live");
      if (flyTo) {
        didFit = true;
        try {
          stripOpsMapFlyQueryFromHash();
        } catch (_) {}
      }
      map.once("load", () => {
        if (!opts.isAlive() || !map) return;
        try {
          map.resize();
        } catch (_) {}
        if (flyTo) {
          try {
            map.jumpTo({
              center: [flyTo.lng, flyTo.lat],
              zoom: clampOpsMapZoom(flyTo.zoom),
              essential: true,
            });
          } catch (_) {}
        }
        syncMarkers([]);
        startLocationsLayer();
        try {
          onAiWarnMove && onAiWarnMove();
        } catch (_) {}
      });
    } catch (_e) {
      opts.setChildMarkup(
        container,
        `<p class="muted" style="padding:12px">Map could not load right now — try Refresh.</p>`,
      );
    }
  };

  const refresh = async () => {
    if (!opts.isAlive()) return;
    try {
      // The Command Center can replace the map canvas during a same-route
      // Home↔Map chrome refresh. Re-mount the React GeoLibre island instead
      // of retaining a live MapLibre object whose canvas is detached.
      const mapContainer = map && typeof map.getContainer === "function"
        ? map.getContainer()
        : null;
      if (map && mapContainer && !mapContainer.isConnected) {
        for (const marker of markers.values()) {
          try {
            marker.remove();
          } catch (_) {}
        }
        markers.clear();
        try {
          const runtime = typeof window !== "undefined" ? window.CrocGeoLibre : null;
          if (runtime && map.__crocReactRootContainer) {
            runtime.unmountMap(map.__crocReactRootContainer);
          } else {
            map.remove();
          }
        } catch (_) {}
        map = null;
        const liveContainer = document.getElementById("cc-overview-map-canvas");
        if (liveContainer) {
          opts.container = liveContainer;
          await init();
        }
      }
      const res = await opts.api(opts.buildMapSnapshotPath(), { timeoutMs: 16000, retries: 0 });
      const all = (res && /** @type {{ items?: unknown[] }} */ (res).items) || [];
      const coords = filterMapSnapshotWithCoords(all);
      const picked = prioritizeOverviewMapMarkers(coords);
      if (!opts.isAlive()) return;
      syncMarkers(picked.items, picked);
    } catch (_) {}
  };

  /**
   * Waiting Auto Sentinel plans → map markers + Allow/Hold sheet (shared data-ai-home-* attrs).
   * @param {unknown[]} plans
   */
  const setAiWaitingPlans = (plans) => {
    if (!map || !opts.isAlive()) return;
    const maplibregl = typeof window !== "undefined" ? window.maplibregl : null;
    if (!maplibregl) return;
    const all = Array.isArray(plans) ? plans : [];
    lastAiAll = all;
    const waiting = all.filter((it) => {
      const st = String((it && it.status) || "").toLowerCase();
      return !st || st === "pending" || st === "open";
    });
    lastAiWaiting = waiting;
    deviceAiPlanById.clear();
    aiPlanByMarkerRef.clear();
    for (const plan of waiting) {
      const deviceId = String((plan && plan.device_id) || "").trim();
      if (!deviceId) continue;
      // Keep highest-risk plan per device when multiple wait.
      const prev = deviceAiPlanById.get(deviceId);
      const score = Number(plan && (plan.risk_score != null ? plan.risk_score : plan.risk));
      const prevScore = Number(prev && (prev.risk_score != null ? prev.risk_score : prev.risk));
      if (!prev || (Number.isFinite(score) && (!Number.isFinite(prevScore) || score > prevScore))) {
        deviceAiPlanById.set(deviceId, plan);
      }
    }
    // Join AI plans → fleet pins: map-snapshot hides device_id, so match on alarm lat/lng.
    for (const [deviceId, plan] of deviceAiPlanById.entries()) {
      let ref = deviceRefById.get(deviceId) || "";
      let coord = deviceCoords.get(deviceId) || null;
      if (!ref || !coord) {
        const alarm = planAlarmCoords(plan);
        if (alarm) {
          ref = nearestMarkerRefByCoords(markerCoordsByRef, alarm.lat, alarm.lng, 150);
          if (ref) {
            const mc = markerCoordsByRef.get(ref);
            coord = mc
              ? { lat: mc.lat, lng: mc.lng, label: mc.label || softDeviceLabel(deviceId) || deviceId }
              : { lat: alarm.lat, lng: alarm.lng, label: softDeviceLabel(deviceId) || deviceId };
            deviceCoords.set(deviceId, coord);
            deviceRefById.set(deviceId, ref);
          } else {
            coord = {
              lat: alarm.lat,
              lng: alarm.lng,
              label: softDeviceLabel(deviceId) || deviceId,
            };
            deviceCoords.set(deviceId, coord);
          }
        }
      }
      if (ref) aiPlanByMarkerRef.set(ref, plan);
    }
    // Refresh crowns on existing fleet pins (no separate floating AI blob when pin exists).
    for (const [ref, mk] of markers.entries()) {
      const el = mk && mk.getElement && mk.getElement();
      if (!el) continue;
      const plan = aiPlanByMarkerRef.get(ref) || null;
      const crownOpts = crownOptsFromPlan(plan);
      const score = crownOpts.aiScore;
      updateCcMarkerAiThreat(el, crownOpts);
      if (plan) {
        el.classList.remove("cc-marker--online", "cc-marker--offline");
        el.classList.add("cc-marker--alert");
        const did = String((plan && plan.device_id) || "").trim();
        if (did) el.dataset.deviceId = did;
        el.dataset.markerRef = ref;
        const base =
          String((markerCoordsByRef.get(ref) || {}).label || "") ||
          String((deviceCoords.get(did) || {}).label || did || "Unit");
        const belief = crownOpts.hypothesis ? String(crownOpts.hypothesis.text || "") : "";
        el.title = `${base} · ${belief || "AI Needs you"}${Number.isFinite(Number(score)) ? ` · ${Math.round(Number(score))}` : ""}`;
      }
    }
    // Also walk deviceRefById for any leftover crown sync.
    for (const [deviceId, ref] of deviceRefById.entries()) {
      const mk = markers.get(ref);
      const el = mk && mk.getElement && mk.getElement();
      if (!el) continue;
      const plan = deviceAiPlanById.get(deviceId);
      if (!plan || aiPlanByMarkerRef.has(ref)) continue;
      updateCcMarkerAiThreat(el, crownOptsFromPlan(plan));
      aiPlanByMarkerRef.set(ref, plan);
    }
    const seen = new Set();
    let firstCoord = null;
    let leadDeviceId = "";
    let leadPlanId = "";
    for (const plan of waiting) {
      const id = String((plan && (plan.incident_id || plan.id || plan.item_id)) || "").trim();
      const deviceId = String((plan && plan.device_id) || "").trim();
      if (!id || !deviceId) continue;
      const coord = deviceCoords.get(deviceId);
      // Prefer device pin crown; only fall back to a free AI marker when the unit has no map pin.
      if (coord && deviceRefById.has(deviceId)) {
        seen.add(id);
        // Drop any earlier free-AI blob once the fleet pin owns this plan.
        const orphan = aiMarkers.get(id);
        if (orphan) {
          try {
            orphan.remove();
          } catch (_) {}
          aiMarkers.delete(id);
        }
        if (!firstCoord) {
          firstCoord = coord;
          leadDeviceId = deviceId;
          leadPlanId = id;
        }
        continue;
      }
      if (!coord) continue;
      seen.add(id);
      if (!firstCoord) {
        firstCoord = coord;
        leadDeviceId = deviceId;
        leadPlanId = id;
      }
      let mk = aiMarkers.get(id);
      if (!mk) {
        const el = document.createElement("div");
        el.className = "cc-marker cc-marker--ai cc-marker--ai-threat";
        el.title = "Auto Sentinel · Needs you · tap for this board";
        el.setAttribute("role", "button");
        el.tabIndex = 0;
        el.innerHTML =
          '<span class="cc-marker__crown" aria-hidden="true">AI</span><span class="cc-marker__ring" aria-hidden="true"></span><span class="cc-marker__core" aria-hidden="true"></span><span class="cc-marker__cap">Needs you</span>';
        const open = (ev) => {
          ev.preventDefault();
          ev.stopPropagation();
          openAiSheet(plan);
        };
        el.addEventListener("click", open);
        el.addEventListener("keydown", (ev) => {
          if (ev.key === "Enter" || ev.key === " ") open(ev);
        });
        mk = new maplibregl.Marker({ element: el, anchor: "center" })
          .setLngLat([coord.lng, coord.lat])
          .addTo(map);
        aiMarkers.set(id, mk);
      } else {
        mk.setLngLat([coord.lng, coord.lat]);
      }
      try {
        const el = mk.getElement && mk.getElement();
        if (el) {
          el.dataset.aiPlanId = id;
          el.dataset.aiDeviceId = deviceId;
          updateCcMarkerAiThreat(el, crownOptsFromPlan(plan));
        }
      } catch (_) {}
    }
    try {
      const mapWrap = opts.container && opts.container.closest && opts.container.closest(".croc-cc-map-wrap");
      const host = mapWrap && mapWrap.querySelector("[data-ai-stage-host]");
      if (host && firstCoord && leadDeviceId) {
        host.setAttribute("data-ai-device-id", leadDeviceId);
        host.setAttribute("data-ai-lng", String(firstCoord.lng));
        host.setAttribute("data-ai-lat", String(firstCoord.lat));
        if (leadPlanId) host.setAttribute("data-ai-plan-id", leadPlanId);
      } else if (host && !firstCoord) {
        host.removeAttribute("data-ai-lng");
        host.removeAttribute("data-ai-lat");
      }
      const root = (opts.container && opts.container.closest("#view"))
        || (opts.container && opts.container.closest(".croc-cc"))
        || document;
      syncMapAiWarnAnchor(root, map);
    } catch (_) {}
    for (const [id, mk] of aiMarkers.entries()) {
      if (!seen.has(id)) {
        try {
          mk.remove();
        } catch (_) {}
        aiMarkers.delete(id);
      }
    }
    if (aiSheet && !aiSheet.hidden) {
      const openId = String(aiSheet.dataset.planId || "");
      if (openId && !seen.has(openId)) {
        const decided = all.find(
          (p) => String((p && (p.incident_id || p.id || p.item_id)) || "") === openId,
        );
        if (decided) {
          const tab = String(aiSheet.dataset.activeTab || "decision");
          openAiSheet(decided, tab === "decision" ? "execution" : tab, { force: true });
        } else {
          // Keep sheet if we still have detail cache (just decided; list lag).
          const cached = aiDetailCache.get(openId);
          if (cached) {
            openAiSheet(cached, "execution", { force: true, detail: cached });
          } else {
            aiSheet.hidden = true;
            try {
              const stage = opts.container && opts.container.closest(".croc-cc-map-stage");
              if (stage) stage.classList.remove("croc-cc-map-stage--saw");
            } catch (_) {}
          }
        }
      }
    }
    try {
      const stage = opts.container && opts.container.closest(".croc-cc-map-stage");
      let chip = stage && stage.querySelector("[data-ai-map-chip]");
      if (stage && seen.size > 0) {
        if (!chip) {
          chip = document.createElement("button");
          chip.type = "button";
          chip.className = "croc-cc-ai-map-chip btn sm btn-tap";
          chip.setAttribute("data-ai-map-chip", "1");
          stage.appendChild(chip);
        }
        chip.textContent = seen.size === 1 ? "1 AI plan" : `${seen.size} AI plans`;
        chip.hidden = false;
        chip.onclick = () => {
          const first = waiting.find((p) => seen.has(String((p && (p.incident_id || p.id)) || "")));
          if (first) openAiSheet(first);
        };
      } else if (chip) {
        chip.hidden = true;
      }
    } catch (_) {}
    if (
      firstCoord &&
      seen.size > 0 &&
      !didAiFly &&
      isOpsMapMalaysiaRegion(firstCoord.lat, firstCoord.lng)
    ) {
      didAiFly = true;
      try {
        map.easeTo({
          center: [firstCoord.lng, firstCoord.lat],
          zoom: clampOpsMapZoom(Math.max(map.getZoom(), 12)),
          duration: 550,
        });
      } catch (_) {}
    }
    // Tip 530: do NOT auto-open the large Spatial AI sheet (covers / splits map).
    // Small WARNING lives in the map-local AI popover; operator taps the AI pin for full sheet.
    if (seen.size > 0 && !didAiAutoOpen) {
      didAiAutoOpen = true;
    }
  };

  try {
    const stage0 = opts.container && opts.container.closest(".croc-cc-map-stage");
    if (stage0 && !stage0.dataset.aiSheetWired) {
      stage0.dataset.aiSheetWired = "1";
      stage0.addEventListener("click", (ev) => {
        const t = ev.target && ev.target.closest && ev.target.closest("[data-ai-map-sheet-close]");
        if (!t || !aiSheet) return;
        aiSheet.hidden = true;
      });
    }
  } catch (_) {}

  const focusLngLat = (lng, lat, zoom = 14) => {
    if (!map || !Number.isFinite(lng) || !Number.isFinite(lat)) return false;
    try {
      map.flyTo({
        center: [Number(lng), Number(lat)],
        zoom: clampOpsMapZoom(Math.max(Number(zoom) || 14, Number(map.getZoom?.() || 0))),
        essential: true,
      });
      return true;
    } catch (_) {
      return false;
    }
  };

  const focusPerson = (userId) => {
    const uid = String(userId || "").trim();
    if (!uid) return false;
    const mk = userMarkers.get(uid);
    if (!mk) return false;
    try {
      const ll = mk.getLngLat && mk.getLngLat();
      if (ll) return focusLngLat(ll.lng, ll.lat, 14);
    } catch (_) {}
    return false;
  };

  return {
    init,
    refresh,
    teardown,
    setAiWaitingPlans,
    focusAiPlan,
    openAiSheet,
    focusLngLat,
    focusPerson,
  };
}
